// Temporary store for image data while it is being uploaded to the server. We
// use this data (in data URL format) to display the image locally.
const TEMP_IMAGE_DATA = new Map<string, string>();

class MediaCommon {
  // Local storage key for the media position preference.
  static mediaPositionPrefKey = 'aha-editor.media.position';

  // Before an image is uploaded we use a temporary URL for the image. The
  // collaborator who uploaded the image can convert the temporary URL to a
  // data URL.
  static isTemporaryDataUrl(url: string): boolean {
    return typeof url === 'string' && url.startsWith('placeholder:');
  }

  // Store data and return a temporary URL for it.
  static storeTemporaryData(dataUrl: string): string {
    const imageId = String(Math.floor(Math.random() * 10000000));
    TEMP_IMAGE_DATA.set(imageId, dataUrl);
    return `placeholder:${imageId}`;
  }

  // Get the data associated with a temporary URL.
  static getTemporaryData(url: string): string | undefined {
    const imageId = url.replace('placeholder:', '');

    return TEMP_IMAGE_DATA.get(imageId);
  }

  // Upload an attachment file to the server and return the URL for the content
  // of the attachment.
  //
  // This is an implementation of the controller uploadMediaFile prop that can
  // be used for development.
  static async uploadMediaFile(
    file: File,
    options: { uploadPath: string }
  ): Promise<string | null> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(options.uploadPath, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      console.warn('Failed to upload attachment');
      return null;
    }

    const result = (await response.json()) as {
      attachmentUrl: string;
    };
    return result.attachmentUrl;
  }

  // Get the width and height of an image from a data URL
  static async measureImageDimensions(
    dataUrl: string
  ): Promise<{ width: number; height: number }> {
    const image = new Image();
    image.src = dataUrl;
    await image.decode();

    return { width: image.width, height: image.height };
  }

  // Get the width and height of a video from a data URL
  static async measureVideoDimensions(
    dataUrl: string
  ): Promise<{ width: number; height: number }> {
    const video = document.createElement('video');

    // Hack: video events (such as loadedmetadata) won't fire for video/quicktime
    // data URLs. Replace with mp4 so we can get dimensions.
    let videoSrc = dataUrl;
    if (videoSrc.startsWith('data:video/quicktime')) {
      videoSrc = videoSrc.replace('data:video/quicktime', 'data:video/mp4');
    }
    video.src = videoSrc;

    const videoMetaData = async (
      video: HTMLVideoElement
    ): Promise<{ width: number; height: number }> => {
      return await new Promise((resolve, reject) => {
        video.addEventListener(
          'loadedmetadata',
          function () {
            const height = this.videoHeight;
            const width = this.videoWidth;
            resolve({ width, height });
          },
          false
        );
      });
    };
    return await videoMetaData(video);
  }
}

export default MediaCommon;
